import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast } from './listPageStyles';

const API_BASE = '/api/reports';

const CATEGORY_TABS = [
  { key: '', label: 'Tümü' },
  { key: 'SALES', label: 'Satış' },
  { key: 'PAYMENT', label: 'Tahsilat' },
  { key: 'OPERATIONS', label: 'Operasyon' },
  { key: 'INVENTORY', label: 'Stok' },
  { key: 'REFERENCE', label: 'Referans' },
];

const CATEGORY_META = {
  SALES: { label: 'Satış', color: '#1d4ed8', bg: 'rgba(0, 115, 234, 0.10)', border: 'rgba(0, 115, 234, 0.32)', line: '#0073ea' },
  PAYMENT: { label: 'Tahsilat', color: '#0f5132', bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.32)', line: '#16a34a' },
  OPERATIONS: { label: 'Operasyon', color: '#92400e', bg: 'rgba(253, 171, 61, 0.14)', border: 'rgba(253, 171, 61, 0.32)', line: '#fdab3d' },
  INVENTORY: { label: 'Stok', color: '#5b21b6', bg: 'rgba(139, 92, 246, 0.12)', border: 'rgba(139, 92, 246, 0.32)', line: '#8b5cf6' },
  REFERENCE: { label: 'Referans', color: '#0f766e', bg: 'rgba(13, 148, 136, 0.12)', border: 'rgba(13, 148, 136, 0.32)', line: '#0d9488' },
};

const PRIORITY_META = {
  A: { label: 'A', color: '#7f1d1d', bg: 'rgba(226, 68, 92, 0.12)', border: 'rgba(226, 68, 92, 0.30)' },
  B: { label: 'B', color: '#92400e', bg: 'rgba(253, 171, 61, 0.14)', border: 'rgba(253, 171, 61, 0.32)' },
  C: { label: 'C', color: '#475569', bg: 'rgba(100, 116, 139, 0.12)', border: 'rgba(100, 116, 139, 0.28)' },
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

// Response zarfı: ApiResponse<List<ReportDto>> → Data doğrudan dizi (items sarmalı yok).
function unwrapReports(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  if (Array.isArray(data)) return data;
  return pick(data, 'items', 'Items') ?? [];
}

function categoryMeta(cat) {
  return CATEGORY_META[cat] || { label: cat || '-', color: '#475569', bg: 'rgba(100,116,139,0.10)', border: 'rgba(100,116,139,0.28)', line: '#64748b' };
}

function Sparkline({ trend, color }) {
  if (!Array.isArray(trend) || trend.length < 2) return null;
  const w = 120, h = 32, pad = 2;
  const min = Math.min(...trend);
  const max = Math.max(...trend);
  const span = max - min || 1;
  const step = (w - pad * 2) / (trend.length - 1);
  const points = trend.map((v, i) => {
    const x = pad + i * step;
    const y = h - pad - ((v - min) / span) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function ReportCard({ report }) {
  const cat = pick(report, 'category', 'Category');
  const cm = categoryMeta(cat);
  const priority = pick(report, 'priority', 'Priority');
  const pm = PRIORITY_META[priority];
  const name = pick(report, 'name', 'Name') || '-';
  const description = pick(report, 'description', 'Description') || '';
  const highlightValue = pick(report, 'highlightValue', 'HighlightValue');
  const highlightLabel = pick(report, 'highlightLabel', 'HighlightLabel');
  const lastUpdated = pick(report, 'lastUpdated', 'LastUpdated') || '';
  const trend = pick(report, 'trend', 'Trend') || [];
  const rows = pick(report, 'rows', 'Rows') || [];
  const isPlaceholder = lastUpdated === 'Yakında';

  return (
    <div style={{
      border: '1px solid #e5e7eb', borderRadius: 14, padding: 16,
      background: isPlaceholder ? '#fbfbfc' : '#fff',
      // gridAutoRows:'1fr' satırı büyütür; kart o satırı DOLDURMALI, yoksa eşitlenen alanın içinde
      // kısa kutu olarak kalır ve görsel olarak hiçbir şey değişmez.
      height: '100%', boxSizing: 'border-box',
      display: 'flex', flexDirection: 'column', gap: 12,
      opacity: isPlaceholder ? 0.72 : 1,
      boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', borderRadius: 999, fontWeight: 800, fontSize: 11,
          background: cm.bg, border: `1px solid ${cm.border}`, color: cm.color,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: 999, background: cm.line }} />
          {cm.label}
        </span>
        {pm && (
          <span style={{
            padding: '2px 8px', borderRadius: 8, fontWeight: 800, fontSize: 11,
            background: pm.bg, border: `1px solid ${pm.border}`, color: pm.color,
          }} title={`Öncelik ${pm.label}`}>
            {pm.label}
          </span>
        )}
      </div>

      <div>
        <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>{name}</div>
        {description && <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 3, lineHeight: 1.4 }}>{description}</div>}
      </div>

      {(highlightValue || trend.length >= 2) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 10 }}>
          <div>
            {highlightValue && <div style={{ fontWeight: 900, fontSize: 24, color: '#0f172a', lineHeight: 1.1 }}>{highlightValue}</div>}
            {highlightLabel && <div style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 600 }}>{highlightLabel}</div>}
          </div>
          <Sparkline trend={trend} color={cm.line} />
        </div>
      )}

      {rows.length > 0 && (
        <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
          {rows.map((r, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
              <span style={{ color: '#6b7280' }}>{pick(r, 'label', 'Label')}</span>
              <span style={{ fontWeight: 700, color: '#0f172a' }}>{pick(r, 'value', 'Value')}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 'auto', fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 5, height: 5, borderRadius: 999, background: isPlaceholder ? '#cbd5e1' : 'var(--success)' }} />
        {isPlaceholder ? 'Yakında' : `Güncellendi: ${lastUpdated}`}
      </div>
    </div>
  );
}

export function ReportsPage() {
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_BASE, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Raporlar alınamadı (${res.status})`);
      const payload = await res.json();
      setReports(unwrapReports(payload));
    } catch (err) {
      showToast('error', err.message || 'Raporlar alınamadı');
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = searchText.trim().toLocaleLowerCase('tr-TR');
    return reports.filter(r => {
      const rc = pick(r, 'category', 'Category') || '';
      if (categoryFilter === '__other__') {
        // Bilinen sekmelerden hiçbirine düşmeyen (ya da kategorisiz) raporlar.
        const known = new Set(CATEGORY_TABS.map((t) => t.key).filter(Boolean));
        if (rc && known.has(rc)) return false;
      } else if (categoryFilter && rc !== categoryFilter) return false;
      if (!needle) return true;
      const fields = [pick(r, 'name', 'Name'), pick(r, 'description', 'Description')];
      return fields.some(v => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    });
  }, [reports, categoryFilter, searchText]);

  const counts = useMemo(() => {
    const live = reports.filter(r => pick(r, 'lastUpdated', 'LastUpdated') !== 'Yakında').length;
    return { total: reports.length, live, soon: reports.length - live };
  }, [reports]);

  // Yusuf Fix — "Tümü'nde 16 var ama kategorileri toplayınca 13 çıkıyor, kalanların statüsü ne?"
  // Veride kayıp rapor YOKTU (SALES 8 + PAYMENT 3 + OPERATIONS 2 + REFERENCE 2 + INVENTORY 1 = 16);
  // sayılar EKRANDA görünmediği için elle sayılıyor ve eksik çıkıyordu. Artık her sekme kendi adedini
  // yazar, "Tümü" toplamı gösterir → aritmetik göz önünde. Veride TANIMSIZ bir kategori belirirse
  // (tab listesinde olmayan bir değer) "Diğer" sekmesi kendiliğinden çıkar ve o raporlar erişilebilir
  // kalır; eskiden hiçbir sekmeye düşmedikleri için gerçekten kaybolurlardı.
  const categoryCounts = useMemo(() => {
    const map = new Map();
    reports.forEach((r) => {
      const key = pick(r, 'category', 'Category') || '';
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [reports]);

  const visibleTabs = useMemo(() => {
    const known = new Set(CATEGORY_TABS.map((t) => t.key).filter(Boolean));
    const unknown = [...categoryCounts.keys()].filter((k) => k && !known.has(k));
    const extra = unknown.length
      ? [{ key: '__other__', label: 'Diğer', keys: unknown }]
      : [];
    return [...CATEGORY_TABS, ...extra];
  }, [categoryCounts]);

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h1 style={s.pageTitle}>Raporlar</h1>
          <p style={s.pageSubtitle}>
            Saha performans rapor kartları — sipariş, ciro, tahsilat, iade ve referans
            özetleri. Değerler server-side aggregate'lerden hesaplanır (mobil Reports ekranının aynası).
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>
            {counts.live} aktif · {counts.soon} yakında
          </span>
          <button type="button" style={s.btnSecondary} onClick={load} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Yenile'}
          </button>
        </div>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          {visibleTabs.map(tab => {
            const n = tab.keys
              ? tab.keys.reduce((sum, k) => sum + (categoryCounts.get(k) || 0), 0)
              : (tab.key ? (categoryCounts.get(tab.key) || 0) : counts.total);
            return (
              <button
                key={tab.key || 'all'}
                type="button"
                onClick={() => setCategoryFilter(tab.key)}
                style={{
                  ...(categoryFilter === tab.key ? s.btnPrimary : s.btnSecondary),
                  padding: '6px 14px', fontSize: 13,
                }}
              >
                {tab.label} ({n})
              </button>
            );
          })}
        </div>
        <div style={s.toolbarRight}>
          <input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Rapor ara..."
            style={s.filterInput}
          />
        </div>
      </div>

      {loading && reports.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>Yükleniyor...</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>Rapor bulunamadı</div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          // Yusuf Fix — TÜM KARTLAR EŞİT BOYUT. Grid öğeleri satır İÇİNDE zaten esniyordu (aynı satırdaki
          // kartlar eşit), ama satırlar arası yükseklik içeriğe göre değişiyordu (ölçüm: 144px ↔ 287px) ve
          // ekran kırık bir mozaik gibi görünüyordu. `1fr` satırların hepsini EN UZUN karta eşitler.
          gridAutoRows: '1fr',
          gap: 16,
        }}>
          {filtered.map((r, idx) => (
            <ReportCard key={pick(r, 'id', 'Id') || pick(r, 'key', 'Key') || idx} report={r} />
          ))}
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default ReportsPage;
